import { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axiosAdmin from "../../../../api/apiAdmin";
import { successToast, errorToast } from "../../toastAlerts/ToastAlerts";
import { TextInput, FileInput } from "../../inputComponents/inputcomp";
import defaultImg from "../../../img/profile.jpg";
import { AdminPrimaryContext } from "../../adminContext/AdminContext";
import { SelectInputValueId } from "../../inputComponents/inputcomp";

//images
import logo from "../../../img/phoneLogo.png";
import HomeDeliveryPriceTable from "../../../../components/HomeDeliveryPriceTable/HomeDeliveryPriceTable";
import TariffEditor from "./TariffEditor";
import {
  currencySymbol,
  normaliseBrackets,
} from "../../../../data/deliveryTariffs";

const InnerAbOfficesPage = () => {
  const { t } = useTranslation();
  //get current office id from params
  const { officeId } = useParams();
  //new ad office data
  const [adOfficeData, setAdOfficeData] = useState({});
  //update res
  const [updateRes, setUpdateRes] = useState(null);
  //data from context
  const { countries } = useContext(AdminPrimaryContext);
  //country options
  const [countryOptions, setCountryOptions] = useState([]);
  const [prices, setPrices] = useState({
    "0-5": "",
    "5-10": "",
    "10-45": "",
    "45-100": " ",
    "100-99999": "",
  });

  const handlePrices = (value, inputName) => {
    setPrices((prev) => ({ ...prev, [inputName]: value }));
  };

  //handle new admin
  const handleNewAdOfficeData = (value, inputName) => {
    setAdOfficeData((prev) => ({ ...prev, [inputName]: value }));
  };

  // Home-delivery tariff editor. Persists as a single brackets[] table on
  // adOfficeData.delivery_tariff — see data/deliveryTariffs.js for the shape.
  // Each row: { from_kg, to_kg, transport_per_kg, delivery_tbilisi_gel,
  // delivery_highland_gel }. Values stay strings while the operator is typing;
  // the backend validator accepts numeric-strings.
  const updateBracket = (idx, key, value) => {
    setAdOfficeData((prev) => {
      const brackets = [...(prev.delivery_tariff?.brackets || [])];
      brackets[idx] = { ...brackets[idx], [key]: value };
      return {
        ...prev,
        delivery_tariff: { ...(prev.delivery_tariff || {}), brackets },
      };
    });
  };
  const addBracket = () => {
    setAdOfficeData((prev) => {
      const existing = prev.delivery_tariff?.brackets || [];
      const last = existing[existing.length - 1];
      const lastTo = Number(last?.to_kg ?? 0);
      // New row starts where the previous ended, extends by 5 kg, and inherits
      // the transport rate so the operator only has to edit what changes.
      const brackets = [
        ...existing,
        {
          from_kg: lastTo,
          to_kg: lastTo + 5,
          transport_per_kg: Number(last?.transport_per_kg ?? 0),
          transport_per_kg_highland:
            last?.transport_per_kg_highland != null
              ? Number(last.transport_per_kg_highland)
              : null,
          delivery_tbilisi_gel: 0,
          delivery_region_gel: 0,
          delivery_highland_gel: 0,
        },
      ];
      return {
        ...prev,
        delivery_tariff: { ...(prev.delivery_tariff || {}), brackets },
      };
    });
  };
  const removeBracket = (idx) => {
    setAdOfficeData((prev) => {
      const brackets = [...(prev.delivery_tariff?.brackets || [])];
      brackets.splice(idx, 1);
      return {
        ...prev,
        delivery_tariff: { ...(prev.delivery_tariff || {}), brackets },
      };
    });
  };
  // Called the first time the operator toggles home_delivery_only ON for an
  // office that has no tariff yet — seeds a sensible starter row so the table
  // isn't empty.
  const ensureSeededTariff = () => {
    setAdOfficeData((prev) => {
      if (prev?.delivery_tariff?.brackets?.length) return prev;
      return {
        ...prev,
        delivery_tariff: {
          brackets: [
            {
              from_kg: 0,
              to_kg: 10,
              transport_per_kg: 0,
              delivery_tbilisi_gel: 0,
              delivery_region_gel: 0,
              delivery_highland_gel: 0,
            },
          ],
        },
      };
    });
  };
  // If the office is on the old shape (tbilisi_per_kg / included_kg / brackets
  // with max_kg), transparently adapt it into the new shape the first time
  // the admin opens the page so their edits save cleanly.
  const migrateLegacyTariffIfNeeded = (tariff) => {
    if (!tariff || typeof tariff !== "object") return tariff;
    const looksLegacy =
      tariff.tbilisi_per_kg != null ||
      tariff.highland_per_kg != null ||
      tariff.included_kg != null ||
      (Array.isArray(tariff.brackets) &&
        tariff.brackets.length > 0 &&
        tariff.brackets[0]?.max_kg != null);
    if (!looksLegacy) return tariff;
    return { brackets: normaliseBrackets(tariff) };
  };

  //ad office data from api
  useEffect(() => {
    axiosAdmin
      .get(`/abroadOffice/${officeId}`)
      .then(({ data }) => {
        setAdOfficeData({
          ...data,
          delivery_tariff: migrateLegacyTariffIfNeeded(data.delivery_tariff),
        });
        try {
          if (JSON.parse(data.prices)) {
            setPrices(JSON.parse(data.prices));
          } else {
            throw new Error();
          }
        } catch {
          setPrices({
            "0-5": "",
            "5-10": "",
            "10-45": "",
            "45-100": " ",
            "100-99999": "",
          });
        }
      })
      .catch((err) => {});
  }, [updateRes]);

  //change country data
  function handleSubmit(event) {
    event.preventDefault();
    const form = event.target;
    const formData = new FormData(form);

    formData.append("country_id", adOfficeData?.country_id);
    formData.append("type", adOfficeData?.type);
    formData.append("currency", adOfficeData?.currency);
    formData.append("prices", JSON.stringify(prices));
    // home_delivery_only flag + delivery_tariff JSON. Backend decodes and
    // stores under the corresponding columns; user side reads them straight
    // from AbroadOffice API responses.
    formData.append(
      "home_delivery_only",
      adOfficeData?.home_delivery_only ? "1" : "0"
    );
    formData.append(
      "delivery_tariff",
      JSON.stringify(adOfficeData?.delivery_tariff || {})
    );

    axiosAdmin
      .post(`/abroadOffice/${officeId}`, formData)
      .then((res) => {
        setUpdateRes(res);
        successToast(t("admin_data_updated_success"));
      })
      .catch((err) => {
        errorToast(t("admin_error_try_again"));
      });
  }

  //country
  useEffect(() => {
    const tmp = countries.map((item) => {
      return { name: item.name, value: item.id };
    });
    setCountryOptions(tmp);
  }, [countries]);

  return (
    <div
      className="xl:ml-[280px] mt-[55px] bg-slate-100 dark:bg-slate-900 
            min-h-screen p-10 sm:p-5 pb-24 sm:pb-24"
    >
      <div className="sm:px-0">
        <form
          onSubmit={(e) => {
            handleSubmit(e);
          }}
          encType="multipart/form-data"
        >
          <div className="w-full rounded-xl bg-white dark:bg-slate-700 p-5">
            <div className="flex items-center justify-between flex-wrap gap-5">
              <div className="flex items-center gap-5 sm:gap-3">
                <div className="w-[100px] h-10">
                  <img src={logo} className="w-full h-full" />
                </div>
                <div>
                  <h2
                    className="text-xl text-slate-600 font-bold dark:text-gray-400
                                sm:text-[16px] smd:text-[16px]"
                  >
                    {t("admin_edit_warehouse")}
                  </h2>
                </div>
              </div>
              <button
                type="submit"
                className="rounded-lg p-3 text-white text-sm bg-violet-400 sm:w-full"
              >
                {t("admin_save")}
              </button>
            </div>

            <div className="flex gap-2">
              <img
                src={
                  adOfficeData?.img !== ""
                    ? `${process.env.REACT_APP_API_URL}/storage/${adOfficeData?.img}`
                    : defaultImg
                }
                className="w-28 h-28 rounded-lg mt-5"
              />
              <FileInput name={t("admin_upload_photo")} inputName={"img"} />
            </div>
            <div className="pt-5 flex gap-5 md:flex-col md:gap-0 sm:flex-col sm:gap-0">
              <div className="w-6/12 sm:w-full md:w-full">
                <TextInput
                  name={t("admin_warehouse")}
                  inputName={"country"}
                  inputSetter={handleNewAdOfficeData}
                  value={adOfficeData?.country}
                />
                <TextInput
                  name={t("admin_address") + "1"}
                  inputName={"adress1"}
                  inputSetter={handleNewAdOfficeData}
                  value={adOfficeData?.adress1}
                />
                <TextInput
                  name={t("admin_phone")}
                  inputName={"phone"}
                  inputSetter={handleNewAdOfficeData}
                  value={adOfficeData?.phone}
                />

                <SelectInputValueId
                  name={t("admin_country")}
                  inputName={"country_id"}
                  inputSetter={handleNewAdOfficeData}
                  options={countryOptions}
                  value={adOfficeData?.country_id}
                />
                <TextInput
                  name={
                    adOfficeData?.id === 9 ? "City / Province" : t("admin_region")
                  }
                  inputName={"street"}
                  inputSetter={handleNewAdOfficeData}
                  value={adOfficeData?.street}
                />
                <TextInput
                  name={adOfficeData?.id === 9 ? "Town / District" : t("admin_city")}
                  inputName={"town"}
                  inputSetter={handleNewAdOfficeData}
                  value={adOfficeData?.town}
                />
                <TextInput
                  name={adOfficeData?.id === 9 ? "Neighberhood" : t("admin_state")}
                  inputName={"state"}
                  inputSetter={handleNewAdOfficeData}
                  value={adOfficeData?.state}
                />
                <TextInput
                  name={t("admin_min_weight")}
                  inputName={"min_weight"}
                  inputSetter={handleNewAdOfficeData}
                  value={adOfficeData?.min_weight}
                />
              </div>
              <div className="w-6/12 sm:w-full md:w-full">
                <TextInput
                  name={t("admin_repack_price")}
                  inputName={"repack_price"}
                  inputSetter={handleNewAdOfficeData}
                  value={adOfficeData?.repack_price}
                />
                {/* Volume weight only from this longest side up, and only
                    when it beats the scale. Empty / 0 = from any size. */}
                <TextInput
                  name={t("admin_volumetric_min_side")}
                  inputName={"volumetric_min_side_cm"}
                  inputSetter={handleNewAdOfficeData}
                  value={adOfficeData?.volumetric_min_side_cm ?? ""}
                />
                <SelectInputValueId
                  name={t("admin_office_type")}
                  inputName={"type"}
                  inputSetter={handleNewAdOfficeData}
                  options={[
                    {
                      name: t("admin_cargo"),
                      value: 1,
                    },
                    {
                      name: t("admin_airplane"),
                      value: 2,
                    },
                    {
                      name: t("admin_ship"),
                      value: 3,
                    },
                  ]}
                  value={adOfficeData?.type}
                />
                <TextInput
                  name={"ZIP"}
                  inputName={"zip_code"}
                  inputSetter={handleNewAdOfficeData}
                  value={adOfficeData?.zip_code}
                />
                <TextInput
                  name={"Adress 1 English"}
                  inputName={"adress2"}
                  inputSetter={handleNewAdOfficeData}
                  value={adOfficeData?.adress2}
                />

                {/* Legacy per-kg + tier pricing. Skipped entirely for
                    home-delivery-only offices — the PriceCalculator ignores
                    these fields there and uses abroad_offices.delivery_tariff
                    instead, so leaving them editable was just confusing. */}
                {!adOfficeData?.home_delivery_only && (
                  <>
                    <TextInput
                      name={t("admin_weight_price")}
                      inputName={"price"}
                      inputSetter={handleNewAdOfficeData}
                      value={adOfficeData?.price}
                      type="number"
                    />
                    <SelectInputValueId
                      name={t("admin_currency")}
                      inputName={"currency"}
                      inputSetter={handleNewAdOfficeData}
                      options={[
                        { name: "GEL", value: "GEL" },
                        { name: "USD", value: "USD" },
                        { name: "EUR", value: "EUR" },
                      ]}
                      value={adOfficeData?.currency}
                    />
                  </>
                )}
                <TextInput
                  name={t("admin_delivery_price")}
                  inputName={"delivery_price"}
                  inputSetter={handleNewAdOfficeData}
                  value={adOfficeData?.delivery_price}
                />
              </div>
            </div>

            {!adOfficeData?.home_delivery_only && (
              <div className="mt-5">
                <p>{t("admin_prices")} </p>
                <div className="grid grid-cols-2 gap-5">
                  <TextInput
                    name={"0-5"}
                    inputName={"0-5"}
                    inputSetter={handlePrices}
                    value={prices["0-5"]}
                  />
                  <TextInput
                    name={"5-10"}
                    inputName={"5-10"}
                    inputSetter={handlePrices}
                    value={prices["5-10"]}
                  />
                  <TextInput
                    name={"10-45"}
                    inputName={"10-45"}
                    inputSetter={handlePrices}
                    value={prices["10-45"]}
                  />
                  <TextInput
                    name={"45-100"}
                    inputName={"45-100"}
                    inputSetter={handlePrices}
                    value={prices["45-100"]}
                  />
                  <TextInput
                    name={"100-99999"}
                    inputName={"100-99999"}
                    inputSetter={handlePrices}
                    value={prices["100-99999"]}
                  />
                </div>

                {/* Scheduled overlay — flat per-kg Tbilisi + Highland that
                    kicks in on `scheduled_from`. Backend replaces the tier
                    lookup above with these numbers for eligible offices once
                    the date passes. Leave blank to keep the tier pricing. */}
                <div className="mt-5 rounded-xl border-2 border-dashed border-indigo-300 bg-indigo-50/40 dark:bg-slate-800/40 p-4">
                  <p className="text-sm font-bold text-slate-700 dark:text-slate-100 mb-1">
                    {t("admin_scheduled_pricing") ||
                      "დაგეგმილი ტარიფის ცვლილება"}
                  </p>
                  <p className="text-xs text-slate-500 mb-3">
                    {t("admin_scheduled_pricing_hint") ||
                      "მითითებული თარიღიდან PriceCalculator იყენებს ცოცხალ ფასს ცხრილის ნაცვლად. მაღალმთიანი ცალკე იბილინგება მომხმარებლის მისამართის მიხედვით."}
                  </p>
                  <div className="grid grid-cols-3 md:grid-cols-1 gap-3">
                    <TextInput
                      name={t("admin_scheduled_from") || "ცოცხალდება (თარიღი)"}
                      inputName={"scheduled_from"}
                      inputSetter={handleNewAdOfficeData}
                      value={
                        adOfficeData?.scheduled_from
                          ? String(adOfficeData.scheduled_from).slice(0, 10)
                          : ""
                      }
                      type="date"
                    />
                    <TextInput
                      name={`${
                        t("admin_price_scheduled_tbilisi") ||
                        "თბილისი / რეგიონები"
                      } ($/${t("admin_kg") || "კგ"})`}
                      inputName={"price_scheduled"}
                      inputSetter={handleNewAdOfficeData}
                      value={adOfficeData?.price_scheduled ?? ""}
                      type="number"
                    />
                    <TextInput
                      name={`${
                        t("admin_price_scheduled_highland") || "მაღალმთიანი"
                      } ($/${t("admin_kg") || "კგ"})`}
                      inputName={"price_scheduled_highland"}
                      inputSetter={handleNewAdOfficeData}
                      value={adOfficeData?.price_scheduled_highland ?? ""}
                      type="number"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="mt-5">
              {/* Home-delivery configuration block. The checkbox toggles the
                  flag; the three numeric fields feed abroad_offices.delivery_tariff.
                  Preview below re-renders live off adOfficeData so the admin
                  can eyeball what changes to per-kg USD produce in GEL. */}
              <div className="mt-6 border-2 border-dashed border-teal-400 rounded-xl p-4 bg-teal-50/40 dark:bg-slate-800/40">
                <label className="flex items-center gap-2 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    className="w-4 h-4"
                    checked={!!adOfficeData?.home_delivery_only}
                    onChange={(e) =>
                      handleNewAdOfficeData(e.target.checked, "home_delivery_only")
                    }
                  />
                  <span className="font-bold text-slate-700 dark:text-slate-100">
                    {t("admin_home_delivery_only") ||
                      "სახლში მიტანით მხოლოდ (Home delivery only)"}
                  </span>
                </label>
                <p className="text-xs text-slate-500 mb-3">
                  {t("admin_home_delivery_hint", {
                    defaultValue:
                      "თუ ჩართულია, ამ საწყობის ამანათი მომხმარებელს პირდაპირ მისამართზე მიაქვთ (ოფისზე გატანა არ ხდება). ფასები საწყობის ვალუტაშია ({{symbol}}) — გათვლაში ხდება ცოცხალი {{code}}→GEL კონვერსია.",
                    symbol: currencySymbol(adOfficeData?.currency),
                    code: (adOfficeData?.currency || "USD").toUpperCase(),
                  })}
                </p>

                {adOfficeData?.home_delivery_only && (
                  <>
                    {/* Single tariff table — one row per weight range with
                        transport (in office currency, applied per-kg on top of
                        the whole weight) + delivery ₾ per zone. All the old
                        separate per_kg / included_kg fields collapse into
                        this one editor. */}
                    <div className="mt-2">
                      <p className="text-sm font-bold text-slate-700 dark:text-slate-100 mb-1">
                        {t("admin_delivery_tariff_table") ||
                          "სახლში მიტანის ტარიფი"}
                      </p>
                      <p className="text-xs text-slate-500 mb-3">
                        {t("admin_delivery_tariff_hint") ||
                          "თითოეული რიგი ერთი წონის დიაპაზონია. ტრანსპორტი ერიცხება წონას საწყობის ვალუტაში; მიტანა (₾) დაემატება მოცემული ზონისთვის."}
                      </p>

                      <TariffEditor
                        brackets={adOfficeData?.delivery_tariff?.brackets}
                        currency={adOfficeData?.currency}
                        onChange={updateBracket}
                        onAdd={() => {
                          ensureSeededTariff();
                          addBracket();
                        }}
                        onRemove={removeBracket}
                      />
                    </div>

                    {adOfficeData?.delivery_tariff?.brackets?.length ? (
                      <div className="mt-4">
                        <HomeDeliveryPriceTable office={adOfficeData} />
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InnerAbOfficesPage;
